import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from "@angular/forms";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandComponent, LabelCommand } from "@tylertech/commands-module";
import { evaluate, LgdValidators, ValueOrGetter } from "@tylertech/lgd-reactive-forms";
import { EventEmitterHelper } from "@tylertech/shared-module";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DragAndDropDirective, FileFormControlValueAccessor } from "../../directives";
import { AttachmentModel } from "../../interfaces";
import { AttachmentService } from "../../services";
import { AttachmentComponent } from "../attachment/attachment.component";

function getFileTypeValidator(acceptedTypesFn?: ValueOrGetter<ReadonlyArray<string>>): ValidatorFn
{
    return maxFileTypeValidator;

    function maxFileTypeValidator(control: AbstractControl<AttachmentModel>): ValidationErrors | null
    {
        const acceptedTypes = evaluate(acceptedTypesFn);
        if (acceptedTypes?.length > 0 && control.value)
        {
            const nameParts = control.value.Filename.split(".");
            const fileExtension = nameParts.length > 1
                ? nameParts.pop().toLowerCase()
                : undefined;

            if (!acceptedTypes.includes(fileExtension))
            {
                return {
                    fileType: {
                        accept: acceptedTypes,
                        actual: fileExtension
                    }
                };
            }
        }
        return null;
    }
}

@Component({
    selector: "attachments-container",
    templateUrl: "./attachmentsContainer.tmpl.html",
    imports: [
        AttachmentComponent,
        CommandComponent,
        LayoutAlignDirective,
        LayoutDirective,
        DragAndDropDirective,
        FileFormControlValueAccessor,
        ReactiveFormsModule,
    ]
})
export class AttachmentsContainerComponent
    implements OnDestroy, OnInit
{
    private $attachmentService = inject(AttachmentService);

    @Input() readonly canEdit: boolean;
    @Input() readonly entityType: string;
    @Input() readonly canAdd: boolean;

    @Input("model") private set boundModel(value: Array<AttachmentModel>)
    {
        this._model = value;

        this._attachments = value
            ? [...value]
            : [];

        this._attachments.sort((a1, a2) =>
        {
            return new Date(a1.AddedDate)?.getTime() - new Date(a2.AddedDate)?.getTime();
        });

        this.attachmentsFormArray.clear();
        this._attachments.forEach(attachment =>
        {
            this.attachmentsFormArray.push(
                new FormControl(
                    attachment,
                    {
                        validators: [
                            LgdValidators.getMaxFileSizeValidator(() => this.$attachmentService.MaxFileSize),
                            getFileTypeValidator(() => this.$attachmentService.getSupportedFileTypes())
                        ]
                    }));
        });
    }

    @Output() readonly add = new EventEmitter<Array<AttachmentModel>>();
    @Output() readonly delete = new EventEmitter<AttachmentModel>();

    @ViewChild("fileInput") private readonly fileInput: ElementRef<HTMLInputElement>;

    private readonly destructor$ = new Subject<void>();
    // private _errors: AttachmentErrors = <AttachmentErrors>{};
    private _model: Array<AttachmentModel>;
    private _attachments: Array<AttachmentModel>;

    readonly addFileCommand: Command;
    readonly formGroup: FormGroup;

    constructor()
    {
        this.formGroup = new FormGroup({
            attachments: new FormArray([]),
            addFile: new FormControl()
        });

        this.addFileCommand = new LabelCommand({
            label: "Select files",
            action: () =>
            {
                this.fileInput.nativeElement.click();
            }
        });
    }

    private get addFileControl(): AbstractControl<ReadonlyArray<AttachmentModel>> { return this.formGroup.get("addFile"); }
    private get attachmentsFormArray(): FormArray<AbstractControl<AttachmentModel>> { return this.formGroup.get("attachments") as FormArray<AbstractControl<AttachmentModel>>; }

    get attachments(): ReadonlyArray<AttachmentModel> { return this._attachments; }

    get canDelete(): boolean { return EventEmitterHelper.hasSubscribers(this.delete); }
    get hasAttachments(): boolean
    {
        return this.attachments?.some(x => !x.IsDeleted);
    }

    get maxFileSize(): number
    {
        return this.$attachmentService.MaxFileSize;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        subscribeToValueChanges(
            this.addFileControl,
            (newValue: ReadonlyArray<AttachmentModel>) =>
            {
                this.add.emit(newValue.filter(x => !x.Error));
            });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges
                .pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    onFileSelected($event: Event): void
    {
        if (!this.fileInput.nativeElement.files || this.fileInput.nativeElement.files.length < 1)
        {
            this.addFileControl.setValue(undefined);
            return;
        }

        this.$attachmentService.ConvertToAttachments(Array.from(this.fileInput.nativeElement.files))
            .then(models =>
            {
                this.addFileControl.setValue(models);
            });
    }

    onDropped(attachments: Array<AttachmentModel>): void
    {
        this.add.emit(attachments);
    }

    onDelete(attachment: AttachmentModel): void
    {
        this.delete.emit(attachment);
    }
}
